import {
  AccountBalance,
  Assignment,
  AttachMoney,
  EmojiEvents,
  TrendingUp,
  Warning,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getFundsDashboardData } from '../../../../../lib/api/fundsDashboard/fundsDashboard';
import EnhacedBox from '../../../../../lib/designSystem/Box';
import HorizontalProgressChart from '../../../../../lib/designSystem/Charts/HorizontalProgressChart';
import StatCard from '../../../../../lib/designSystem/Charts/StatCard';
import CenteredCircularProgress from '../../../../../lib/designSystem/CircularProgress';
import {
  formatMoney,
  showAlert as showAlertUtil,
} from '../../../../../lib/functions/utils';
import DueSoonChart from './components/DueSoonChart';
import FundsComparisonChart from './components/FundsComparisonChart';
import OverdueByDaysChart from './components/OverdueByDaysChart';
import OverdueSummary from './components/OverdueSummary';
import PaymentsDistributionChart from './components/PaymentsDistributionChart';
import PaymentTrendsChart from './components/PaymentTrendsChart';
import TopOverdueTable from './components/TopOverdueTable';
import TrendsChart from './components/TrendsChart';

const FundSummary: React.FC = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getFundsDashboardData();
      setDashboardData(data);
    } catch (error) {
      setError('Error al cargar los datos del dashboard de fondos');
      showAlertUtil(
        dispatch,
        'error',
        'Error al cargar los datos del dashboard de fondos',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  if (loading) return <CenteredCircularProgress />;

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h5" color="error" sx={{ mb: 2 }}>
          Error al cargar el dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {error}
        </Typography>
        <Button variant="contained" onClick={fetchDashboardData} sx={{ mr: 2 }}>
          Reintentar
        </Button>
        <Button variant="outlined" onClick={() => setError(null)}>
          Cerrar
        </Button>
      </Box>
    );
  }

  if (!dashboardData) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h5" color="text.secondary" sx={{ mb: 2 }}>
          No hay datos disponibles
        </Typography>
        <Typography variant="body1" color="text.secondary">
          No se encontraron datos para mostrar en el dashboard.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Sección de Tendencias */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Box sx={{ height: 450 }}>
            <TrendsChart data={dashboardData?.nomineesAnalytics} />
          </Box>
        </Grid>
      </Grid>

      {/* Sección de Resumen de Nóminas */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <StatCard
                label="Total Nóminas"
                value={dashboardData?.nomineesSummary?.totalNominees || 0}
                valueLabel=""
                color="#0F23DF"
                icon={<Assignment sx={{ fontSize: 24 }} />}
              />
            </Grid>
            <Grid item xs={12}>
              <StatCard
                label="Nóminas Activas"
                value={dashboardData?.nomineesSummary?.activeNominees || 0}
                valueLabel={`(${(
                  (dashboardData?.nomineesSummary?.activeNominees /
                    dashboardData?.nomineesSummary?.totalNominees) *
                    100 || 0
                ).toFixed(1)}%)`}
                color="#03B4DC"
                icon={<TrendingUp sx={{ fontSize: 24 }} />}
              />
            </Grid>
            <Grid item xs={12}>
              <StatCard
                label="Total Reventas"
                value={dashboardData?.nomineesSummary?.totalResells || 0}
                valueLabel=""
                color="#07D2FE"
                icon={<AccountBalance sx={{ fontSize: 24 }} />}
              />
            </Grid>
            <Grid item xs={12}>
              <StatCard
                label="Total en Mora"
                value={dashboardData?.overdueAndDueDates?.totalOverdue || 0}
                valueLabel="reventas"
                color="#d32f2f"
                icon={<Warning sx={{ fontSize: 24 }} />}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} md={9}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <StatCard
                label="Monto Total de Nóminas"
                value={
                  formatMoney(dashboardData?.nomineesSummary?.totalAmount) ?? 0
                }
                valueLabel="CLP"
                color="#FFC400"
                icon={<AttachMoney sx={{ fontSize: 24 }} />}
              />
            </Grid>
            <Grid item xs={12}>
              <StatCard
                label="Monto en Mora"
                value={
                  formatMoney(
                    dashboardData?.overdueAndDueDates?.totalOverdueAmount,
                  ) ?? 0
                }
                valueLabel="CLP"
                color="#d32f2f"
                icon={<Warning sx={{ fontSize: 24 }} />}
              />
            </Grid>
            <Grid item xs={12}>
              <HorizontalProgressChart
                data={
                  dashboardData?.resellsConsolidated?.comparison
                    ?.individualVsNominees || {
                    individualPercentage: 0,
                    nomineesPercentage: 0,
                  }
                }
                title="Reventas Individuales vs por Nóminas"
                icon={<TrendingUp sx={{ fontSize: 18 }} />}
                color="#03B4DC"
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Sección de Comparación de Fondos y Pagos */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={6}>
          <FundsComparisonChart
            data={dashboardData?.nomineesAnalytics}
            overdueData={dashboardData?.overdueAndDueDates}
          />
        </Grid>

        <Grid item xs={12} lg={6}>
          <Box>
            <PaymentsDistributionChart
              data={dashboardData?.paymentsConsolidated}
            />
            <Box sx={{ mt: 2 }}>
              <StatCard
                label="Monto Total de Pagos Consolidados"
                value={formatMoney(
                  dashboardData?.paymentsConsolidated?.totalAmount || 0,
                )}
                valueLabel="CLP"
                color="#2e7d32"
                icon={<AttachMoney sx={{ fontSize: 24 }} />}
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <StatCard
                label="Monto Promedio de Pago"
                value={formatMoney(
                  dashboardData?.paymentsConsolidated?.averagePaymentAmount ||
                    0,
                )}
                valueLabel="CLP"
                color="#03B4DC"
                icon={<AttachMoney sx={{ fontSize: 24 }} />}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Sección de Gráficos de Mora con OverdueSummary */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} lg={4.5}>
          <OverdueByDaysChart data={dashboardData?.overdueAndDueDates} />
        </Grid>
        <Grid item xs={12} lg={4.5}>
          <DueSoonChart data={dashboardData?.overdueAndDueDates} />
        </Grid>
        <Grid item xs={12} lg={3}>
          <OverdueSummary data={dashboardData?.overdueAndDueDates} />
        </Grid>
      </Grid>

      {/* Sección de Tendencias de Pagos y Estadísticas de Pagos */}
      <Grid container spacing={3} sx={{ mt: 3 }}>
        <Grid item xs={12} lg={8}>
          <PaymentTrendsChart data={dashboardData?.paymentsConsolidated} />
        </Grid>
        <Grid item xs={12} lg={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <StatCard
              label="Total Pagos"
              value={dashboardData?.paymentsConsolidated?.totalPayments || 0}
              valueLabel=""
              color="#1976d2"
              icon={<Assignment sx={{ fontSize: 24 }} />}
            />
            <StatCard
              label="Pagos Pendientes"
              value={dashboardData?.paymentsConsolidated?.pendingPayments || 0}
              valueLabel={`(${(
                (dashboardData?.paymentsConsolidated?.pendingPayments /
                  dashboardData?.paymentsConsolidated?.totalPayments) *
                  100 || 0
              ).toFixed(1)}%)`}
              color="#ed6c02"
              icon={<TrendingUp sx={{ fontSize: 24 }} />}
            />
            <StatCard
              label="Días Promedio de Pago"
              value={
                dashboardData?.paymentsConsolidated?.averagePaymentTime || 0
              }
              valueLabel="días"
              color="#0288d1"
              icon={<AccountBalance sx={{ fontSize: 24 }} />}
            />
          </Box>
        </Grid>
      </Grid>

      {/* Sección de Top Nóminas y Top Reventas en Mora */}
      <Grid container spacing={3} sx={{ mt: 3 }}>
        <Grid item xs={12} lg={6}>
          <EnhacedBox>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(255, 196, 0, 0.12)',
                }}
              >
                <EmojiEvents sx={{ fontSize: 18, color: '#FFC400' }} />
              </div>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: '1.1rem',
                }}
              >
                Top Nóminas por Rendimiento
              </Typography>
            </div>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f8f9fa' }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                      ID
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 600, fontSize: '0.875rem' }}
                    >
                      Reventas
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: 600, fontSize: '0.875rem' }}
                    >
                      Monto Total
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dashboardData?.nomineesSummary?.topNominees?.map(
                    (nominee: any, index: number) => (
                      <TableRow
                        key={nominee.nomineeId || index}
                        sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}
                      >
                        <TableCell
                          component="th"
                          scope="row"
                          sx={{ fontWeight: 500, fontSize: '0.875rem' }}
                        >
                          {nominee.nomineeId}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 500, fontSize: '0.875rem' }}
                        >
                          {nominee.resellsCount}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontWeight: 500,
                            color: 'success.main',
                            fontSize: '0.875rem',
                          }}
                        >
                          ${(nominee.totalAmount / 1000000).toFixed(1)}M
                        </TableCell>
                      </TableRow>
                    ),
                  ) || []}
                </TableBody>
              </Table>
            </TableContainer>
          </EnhacedBox>
        </Grid>
        <Grid item xs={12} lg={6}>
          <TopOverdueTable data={dashboardData?.overdueAndDueDates} />
        </Grid>
      </Grid>
    </Box>
  );
};

export default FundSummary;
